<script setup lang="ts">
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { scroll, SECTION_SCROLL_OFFSET } from '@/shared/utils/scrollBehavior';
import AppRating from '@/features/AppRating/index';
import type { ILawyerCardFeedbackProps } from '@/widgets/LawyerCard/model/types/lawyerCard';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';

withDefaults(defineProps<ILawyerCardFeedbackProps>(), {
  isPushToFeedbacks: false,
  isLawyerCompanyCard: false,
});

const router = useRouter();
const isMobile: boolean = useIsMobile();
const lawyerInfo = inject<ILawyerCardInfo>('lawyerInfo');
const isShowReviewsCount = inject<boolean>('isShowReviewsCount', true);

const feedbacksCount: ComputedRef<number> = computed(
  () => lawyerInfo?.feedbackCount ?? 0,
);
const feedbackRating: ComputedRef<number> = computed(() =>
  feedbacksCount.value > 0 ? (lawyerInfo?.feedbackRating ?? 0) : 0,
);

const linkToProfileFeedbacks: ComputedRef<string> = computed(() => {
  const region = lawyerInfo?.addresses?.length ? lawyerInfo.addresses[0]?.city?.slug : '';
  const type = lawyerInfo?.company ? 'organizations' : 'lawyers';
  return `${ROOT_CATALOG_URL}geo/${region}/${type}/${lawyerInfo?.slug}/#feedbacks`;
});

const scrollTo = (): void => {
  const feedbacks = document.querySelector('#feedbacks');
  scroll(feedbacks, SECTION_SCROLL_OFFSET);
};

const goToProfileFeedbacks = (): void => {
  router.push(linkToProfileFeedbacks.value);
};
</script>

<template>
  <div :class="['lawyer-card-feedback', { _mobile: isMobile }]">
    <div class="lawyer-card-feedback__rating">
      <AppRating disabled :model-value="feedbackRating" />
    </div>
    <template v-if="isShowReviewsCount">
      <NuxtLink
        v-if="isPushToFeedbacks && !isLawyerCompanyCard"
        class="lawyer-card-feedback__count"
        :to="linkToProfileFeedbacks"
      >
        Отзывы
        <strong>({{ feedbacksCount }})</strong>
      </NuxtLink>
      <button
        v-else
        class="lawyer-card-feedback__count"
        :aria-label="`Отзывы (${feedbacksCount}): перейти к отзывам`"
        @click.stop.prevent="isPushToFeedbacks ? goToProfileFeedbacks() : scrollTo()"
      >
        Отзывы
        <strong>({{ feedbacksCount }})</strong>
      </button>
    </template>
  </div>
</template>

<style lang="less">
.lawyer-card-feedback {
  &__rating {
    margin-bottom: @main-size;
  }

  &__count {
    .color-change-hover();

    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 600;
  }

  &._mobile &__rating svg {
    width: 13px;
    height: 13px;
  }

  &._mobile &__count {
    white-space: nowrap;
  }
}
</style>
